import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  Button,
  Anchor,
  Stack,
  Alert,
  Center,
  Box,
  Image,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const { requestPasswordReset } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    validate: zodResolver(forgotPasswordSchema),
    initialValues: {
      email: '',
    },
  });

  const handleSubmit = async (values: ForgotPasswordFormValues) => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await requestPasswordReset(values.email);

      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Failed to send reset email. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <Container size={420}>
          <Paper withBorder shadow="xl" p="xl" radius="md">
            <Center>
              <IconCheck size={60} color="green" />
            </Center>
            <Title order={2} ta="center" mt="md" mb="md">
              Check your email
            </Title>
            <Text c="dimmed" ta="center" mb="xl">
              We've sent you a password reset link. Please check your email and follow the
              instructions.
            </Text>
            <Button fullWidth component={Link} to="/login">
              Back to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <Container size={420}>
        <Center mb="xl">
          <Image src="/logo-inverse.png" alt="TrustPuma" h={80} w="auto" />
        </Center>

        <Paper withBorder shadow="xl" p="xl" radius="md">
          <Title order={2} ta="center" mb="md">
            Forgot your password?
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb="xl">
            Enter your email address and we'll send you a link to reset your password.
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Email"
                placeholder="your@email.com"
                required
                {...form.getInputProps('email')}
              />

              <Button type="submit" fullWidth mt="md" loading={isLoading}>
                Send reset link
              </Button>
            </Stack>
          </form>

          <Text c="dimmed" size="sm" ta="center" mt="xl">
            Remember your password?{' '}
            <Anchor component={Link} to="/login" fw={500}>
              Sign in
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Box>
  );
}
